import { Link, createFileRoute } from "@tanstack/react-router";
import { BookOpen, Coins, GitBranch, Hexagon, Layers, Sparkles } from "lucide-react";
import { SiteHeader } from "@/components/site-header";

export const Route = createFileRoute("/kinh-dich-chi-tiet")({
  head: () => ({
    meta: [
      { title: "Giải Thích Kinh Dịch - 64 Quẻ Dịch, Âm Dương, Bát Quái" },
      {
        name: "description",
        content:
          "Tìm hiểu Kinh Dịch, Âm Dương, Bát Quái, 64 quẻ, hào động và cách luận giải một quẻ. Tìm hiểu ngay!",
      },
      { property: "og:title", content: "Giải Thích Kinh Dịch - 64 Quẻ Dịch, Âm Dương, Bát Quái" },
      {
        property: "og:description",
        content: "Nhập môn Kinh Dịch: Âm Dương, Bát Quái, 64 quẻ và cách đọc hào động.",
      },
      { property: "og:url", content: "https://boide.online/kinh-dich-chi-tiet" },
    ],
    links: [{ rel: "canonical", href: "https://boide.online/kinh-dich-chi-tiet" }],
  }),
  component: IChingDetailPage,
});

const concepts = [
  {
    title: "Âm Dương",
    icon: Hexagon,
    body: "Hai trạng thái nền tảng của biến dịch: âm là tĩnh, nhu, thu nhận; dương là động, cương, phát khởi. Mọi quẻ đều là tổ hợp của hai lực này.",
  },
  {
    title: "Bát Quái",
    icon: Layers,
    body: "Tám quái Càn, Khôn, Chấn, Tốn, Khảm, Ly, Cấn, Đoài là tám biểu tượng gốc để mô tả thiên nhiên, con người và hoàn cảnh.",
  },
  {
    title: "64 Quẻ",
    icon: Coins,
    body: "Mỗi quẻ kép gồm hai quái chồng lên nhau, tạo thành 64 tình huống biến hóa. Quẻ không chỉ nói kết quả mà còn mô tả thế vận động.",
  },
  {
    title: "Hào Động",
    icon: GitBranch,
    body: "Hào động là điểm đang biến trong quẻ. Khi hào động đổi âm dương sẽ sinh quẻ biến, cho biết xu hướng tiếp theo của sự việc.",
  },
];

const readingSteps = [
  "Xác định câu hỏi thật rõ: hỏi việc gì, trong bối cảnh nào, cần quyết định điều gì.",
  "Xem quẻ chủ để hiểu tình thế hiện tại và khí chính của sự việc.",
  "Xem hào động để tìm điểm then chốt, nơi sự việc đang chuyển biến mạnh nhất.",
  "Xem quẻ biến để hiểu xu hướng nếu tình thế tiếp tục phát triển.",
  "Đối chiếu lời quẻ với thực tế, chọn hành động tỉnh táo thay vì phó mặc cho kết quả.",
];

function IChingDetailPage() {
  return (
    <>
      <main className="flex-1 overflow-y-auto px-6 py-8 pb-24 lg:px-12 lg:pb-12">
        <div className="mx-auto max-w-6xl space-y-8">
          <section className="rounded-3xl border border-gold-soft bg-card/50 p-8 shadow-mystic backdrop-blur">
            <div className="max-w-3xl">
              <span className="inline-flex items-center gap-2 rounded-full border border-gold-soft/60 bg-background/40 px-3 py-1 text-xs uppercase tracking-[0.25em] text-primary">
                <BookOpen className="h-3.5 w-3.5" /> Nhập môn
              </span>
              <h1 className="mt-5 text-3xl font-semibold text-gold-gradient lg:text-5xl">
                Kinh Dịch là bản đồ của sự biến đổi.
              </h1>
              <p className="mt-5 text-base leading-8 text-foreground/85 lg:text-lg">
                Kinh Dịch không chỉ là phương pháp dự đoán. Đó là cách quan sát sự vật qua chu kỳ
                động - tĩnh, thịnh - suy, tiến - lui. Một quẻ tốt không có nghĩa là được chủ quan;
                một quẻ khó không có nghĩa là hết đường, mà chỉ ra điểm cần điều chỉnh.
              </p>
            </div>
          </section>

          <section className="grid gap-4 md:grid-cols-2">
            {concepts.map((item) => (
              <article
                key={item.title}
                className="rounded-2xl border border-gold-soft/60 bg-background/30 p-5 backdrop-blur"
              >
                <div className="flex items-center gap-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/15 text-primary">
                    <item.icon className="h-4 w-4" />
                  </div>
                  <h3 className="text-lg font-semibold text-foreground">{item.title}</h3>
                </div>
                <p className="mt-3 text-sm leading-7 text-muted-foreground">{item.body}</p>
              </article>
            ))}
          </section>

          <section className="grid gap-6 lg:grid-cols-[1.1fr_0.9fr]">
            <article className="rounded-2xl border border-gold-soft bg-card/40 p-6 backdrop-blur">
              <span className="text-xs uppercase tracking-[0.25em] text-primary">
                Cách luận một quẻ
              </span>
              <ol className="mt-5 space-y-4">
                {readingSteps.map((step, index) => (
                  <li
                    key={step}
                    className="flex gap-4 rounded-xl border border-primary/15 bg-background/25 p-4"
                  >
                    <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-gold-gradient text-sm font-semibold text-primary-foreground">
                      {index + 1}
                    </span>
                    <p className="text-sm leading-7 text-foreground/85">{step}</p>
                  </li>
                ))}
              </ol>
            </article>

            <article className="rounded-2xl border border-gold-soft bg-card/40 p-6 backdrop-blur">
              <span className="text-xs uppercase tracking-[0.25em] text-primary">Ví dụ</span>
              <h3 className="mt-3 text-2xl font-semibold text-gold-gradient">Hỏi về công việc</h3>
              <div className="mt-4 space-y-3 text-sm leading-7 text-foreground/85">
                <p>
                  Nếu quẻ chủ nói về chờ thời, lời khuyên không phải là ngồi yên, mà là chuẩn bị
                  nguồn lực, quan sát thời điểm và tránh quyết định nóng vội.
                </p>
                <p>
                  Nếu hào động nằm ở giữa quẻ, trọng tâm thường ở giai đoạn chuyển tiếp: cần sửa
                  cách làm, điều chỉnh quan hệ hoặc thay đổi nhịp tiến.
                </p>
                <p>
                  Quẻ biến cho biết xu hướng sau khi điểm động đã chuyển hóa, giúp người hỏi chọn
                  hành động phù hợp hơn.
                </p>
              </div>
            </article>
          </section>

          <section className="rounded-2xl border border-gold-soft bg-background/30 p-6 text-center backdrop-blur">
            <Sparkles className="mx-auto h-8 w-8 text-primary" />
            <h3 className="mt-3 text-2xl font-semibold text-gold-gradient">Sẵn sàng gieo quẻ?</h3>
            <p className="mx-auto mt-3 max-w-2xl text-sm leading-7 text-muted-foreground">
              Hãy dùng Kinh Dịch như một công cụ phản tư: hỏi rõ, đọc chậm, rồi tự chịu trách nhiệm
              với lựa chọn của mình.
            </p>
            <Link
              to="/kinh-dich"
              className="mt-5 inline-flex rounded-full bg-gold-gradient px-6 py-2.5 text-sm font-medium text-primary-foreground shadow-gold transition hover:scale-[1.02]"
            >
              Gieo quẻ Kinh Dịch
            </Link>
          </section>
        </div>
      </main>
    </>
  );
}
